import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  DeferredRevenue,
  DeferredRevenueContracts,
  DownloadedFile,
  Journal,
  JournalBalances,
  JournalEntriesPage,
  JournalEntriesQuery,
  JournalEntry,
  JournalExactExportBody,
  JournalExportQuery,
  JournalOverviewQuery,
  JournalPeriod,
  JournalPeriods,
  JournalRangeQuery,
  JournalSources,
  JournalSourcesQuery,
  JournalSummaryQuery,
  LedgerFacts,
  LedgerFactsQuery,
  LedgerLines,
  LedgerLinesQuery,
} from '@digilize/shared/definitions/src';
import { map, Observable } from 'rxjs';

import { toDownloadedFile } from './downloaded-file';

/** Leaves out empty filters, so the BE never reads `null` or `undefined` as a value. */
export const toQueryParams = (query: object): HttpParams =>
  Object.entries(query).reduce(
    (params, [key, value]) =>
      value === null || value === undefined || value === '' ? params : params.set(key, String(value)),
    new HttpParams()
  );

@Injectable({ providedIn: 'root' })
export class JournalApiService {
  constructor(private http: HttpClient) {}

  private base(companyId: string): string {
    return `/api/v1.0/companies/${companyId}/journal`;
  }

  getPeriods(companyId: string): Observable<JournalPeriods> {
    return this.http.get<JournalPeriods>(`${this.base(companyId)}/periods`);
  }

  closePeriod(companyId: string, periodId: string): Observable<JournalPeriod> {
    return this.http.post<JournalPeriod>(`${this.base(companyId)}/periods/${periodId}/close`, {});
  }

  reopenPeriod(companyId: string, periodId: string): Observable<JournalPeriod> {
    return this.http.post<JournalPeriod>(`${this.base(companyId)}/periods/${periodId}/reopen`, {});
  }

  /** With `include_entries: false` the overview: groups and counts, no entries (a month of them is megabytes). */
  getJournal(companyId: string, query: JournalOverviewQuery): Observable<Journal> {
    return this.http.get<Journal>(this.base(companyId), { params: toQueryParams(query) });
  }

  /** One page of the entries, e.g. of one group with its dates; `include_lines: false` leaves out the lines. */
  getJournalEntries(companyId: string, query: JournalEntriesQuery): Observable<JournalEntriesPage> {
    return this.http.get<JournalEntriesPage>(`${this.base(companyId)}/entries`, { params: toQueryParams(query) });
  }

  /** One entry with its lines. */
  getJournalEntry(companyId: string, entryId: string, entryDate: string): Observable<JournalEntry> {
    return this.http.get<JournalEntry>(`${this.base(companyId)}/entries/${entryId}`, {
      params: toQueryParams({ entry_date: entryDate }),
    });
  }

  getSources(companyId: string, query: JournalSourcesQuery): Observable<JournalSources> {
    return this.http.get<JournalSources>(`${this.base(companyId)}/sources`, { params: toQueryParams(query) });
  }

  getBalances(companyId: string, query: JournalRangeQuery): Observable<JournalBalances> {
    return this.http.get<JournalBalances>(`${this.base(companyId)}/balances`, { params: toQueryParams(query) });
  }

  /** One Balances account: every stored row on it, one line each. */
  getLedgerLines(companyId: string, query: LedgerLinesQuery): Observable<LedgerLines> {
    return this.http.get<LedgerLines>(`${this.base(companyId)}/balances/lines`, { params: toQueryParams(query) });
  }

  getLedgerFacts(companyId: string, query: LedgerFactsQuery): Observable<LedgerFacts> {
    return this.http.get<LedgerFacts>(`${this.base(companyId)}/balances/facts`, { params: toQueryParams(query) });
  }

  getDeferredRevenue(companyId: string, periodId: string): Observable<DeferredRevenue> {
    return this.http.get<DeferredRevenue>(`${this.base(companyId)}/deferred-revenue`, {
      params: toQueryParams({ period_id: periodId }),
    });
  }

  getDeferredRevenueContracts(
    companyId: string,
    productId: string,
    periodId: string
  ): Observable<DeferredRevenueContracts> {
    return this.http.get<DeferredRevenueContracts>(`${this.base(companyId)}/deferred-revenue/${productId}/contracts`, {
      params: toQueryParams({ period_id: periodId }),
    });
  }

  /** With `from_date` / `to_date`: one group of the range. */
  exportJournal(companyId: string, query: JournalExportQuery): Observable<DownloadedFile> {
    return this.download(`${this.base(companyId)}/export`, toQueryParams(query));
  }

  /** The journal summed per day or journal group and G/L account; stamps nothing. */
  exportJournalSummary(companyId: string, query: JournalSummaryQuery): Observable<DownloadedFile> {
    return this.download(`${this.base(companyId)}/export/summary`, toQueryParams(query));
  }

  /** Marks every document of the range (or of the one group) exported on success; errors come back as a JSON blob. */
  exportExact(companyId: string, body: JournalExactExportBody): Observable<DownloadedFile> {
    return this.http
      .post(`${this.base(companyId)}/export/exact`, body, { observe: 'response', responseType: 'blob' })
      .pipe(map(toDownloadedFile));
  }

  exportBalances(companyId: string, query: JournalRangeQuery): Observable<DownloadedFile> {
    return this.download(`${this.base(companyId)}/balances/export`, toQueryParams(query));
  }

  exportDeferredRevenue(companyId: string, periodId: string): Observable<DownloadedFile> {
    return this.download(`${this.base(companyId)}/deferred-revenue/export`, toQueryParams({ period_id: periodId }));
  }

  private download(url: string, params: HttpParams): Observable<DownloadedFile> {
    return this.http.get(url, { params, observe: 'response', responseType: 'blob' }).pipe(map(toDownloadedFile));
  }
}
